import { CommonPage, Content, LoadableContent } from "common/Grid";
import { MainForm } from "../Common/MainForm";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetSiteConfigQuery, useUpdateSiteConfigMutation } from "store/action/site-config";
import { useEffect, useState } from "react";
import { useGetFileUploadMutation } from "store/action/inquiryApi";
import { captureComponentError } from "utils/sentry";

// Sensitive fields (secrets/tokens) skip client-side required validation and are
// validated server-side instead; map the backend's rejected keys back onto the form
// the same way a required-field error would show.
const buildInvalidKeyErrors = (invalidKeys, message) =>
  Object.fromEntries(invalidKeys.map((key) => [key, message || "Invalid value"]));

// The backend returns invalidKeys either at the top level or nested under
// invalidSites[0] (bulk-style response shape), depending on the endpoint.
const extractInvalidKeys = (payload) =>
  payload?.invalidKeys || payload?.invalidSites?.[0]?.invalidKeys;

const Page = () => {
  const [searchParams] = useSearchParams();
  const siteid = searchParams.get("siteid");
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState({});

  const [updateSiteConfig, { isLoading }] = useUpdateSiteConfigMutation();
  const [uploadFile] = useGetFileUploadMutation();
  const { data: siteConfigData, isLoading: isLoadingConfig, refetch } = useGetSiteConfigQuery(siteid, {
    skip: !siteid,
    refetchOnMountOrArgChange: true,
    keepUnusedDataFor: 0
  });

  const [userInput, setUserInput] = useState({
    squareConfig: {},
    triniteq: {},
    webhook: {},
    appConfig: {},
    mailSettings: {},
    tevalis: {},
    twilio: {},
    oneSignal: {},
    payStack: {},
    stripeConfig: {},
    clientName: "",
    filePwd: "",
    // siteName: "",
    reportUrl: "",
    paymentProvider: "",
    locationId: "",
    franchiseid: "",
    locale: "",
    squareSync: false,
    triniteqPos: false,
    tevalisPos: false,
    siteid: "",
  });

  useEffect(() => {
    if (siteConfigData?.data) setUserInput(siteConfigData.data);
  }, [siteConfigData]);

  function generateHexColorVariants(inputColor) {
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));

    const rgbToHex = (r, g, b) =>
      `#${[r, g, b]
        .map(v => clamp(v).toString(16).padStart(2, "0"))
        .join("")}`;

    const parseColor = (color) => {
      color = color.trim();

      // HEX (#fff | #ffffff)
      if (color.startsWith("#")) {
        let hex = color.slice(1);
        if (hex.length === 3) {
          hex = hex.split("").map(c => c + c).join("");
        }
        const num = parseInt(hex, 16);
        return {
          r: (num >> 16) & 255,
          g: (num >> 8) & 255,
          b: num & 255,
        };
      }

      // RGB / RGBA
      const match = color.match(/rgba?\(([^)]+)\)/);
      if (match) {
        const [r, g, b] = match[1].split(",").map(v => parseFloat(v));
        return { r, g, b };
      }

      throw new Error("Unsupported color format");
    };

    const { r, g, b } = parseColor(inputColor);

    return {
      mainColor: rgbToHex(r, g, b),
      hoverColor: rgbToHex(r - 20, g - 20, b - 20),
      disabledColor: rgbToHex(r + 40, g + 40, b + 40),
    };
  }

  const handleSubmit = async () => {
    try {
      setPageLoading(true);
      setApiErrors({});

      const getFileType = (dataUrl) => {
        if (!dataUrl || typeof dataUrl !== "string") return "png";
        const mime = dataUrl.split(";")[0].split(":")[1] || "";
        if (mime.includes("icon") || mime.includes("x-icon") || mime.includes("vnd.microsoft.icon")) return "ico";
        if (mime.includes("jpeg")) return "jpg";
        return mime.split("/")[1] || "png";
      };

      let appIconUrl = userInput?.appIcon?.url || userInput?.appIcon;

      if (typeof appIconUrl === "string" && appIconUrl.startsWith("data:")) {
        const logoUploadResponse = await uploadFile({
          base64: appIconUrl,
          type: getFileType(appIconUrl)
        }).unwrap();
        if (logoUploadResponse?.status === "SUCCESS") {
          appIconUrl = logoUploadResponse.data?.url || logoUploadResponse.data;
        } else {
          toast.error("App icon upload failed");
          setPageLoading(false);
          return;
        }
      }

      let siteBannerUrl = userInput?.appConfig?.siteBanner;

      if (typeof siteBannerUrl === "string" && siteBannerUrl.startsWith("data:")) {
        const bannerUploadResponse = await uploadFile({
          base64: siteBannerUrl,
          type: "png"
        }).unwrap();
        if (bannerUploadResponse?.status === "SUCCESS") {
          siteBannerUrl = bannerUploadResponse.data?.url || bannerUploadResponse.data;
        } else {
          toast.error("Site banner upload failed");
          setPageLoading(false);
          return;
        }
      }

      const primaryColor = userInput?.appConfig?.colors?.primaryColor;
      const variants = generateHexColorVariants(primaryColor);

      const payload = await updateSiteConfig({
        body: {
          ...userInput,
          appIcon: appIconUrl,
          appConfig: {
            ...userInput.appConfig,
            siteBanner: siteBannerUrl,
            colors: {
              ...userInput.appConfig?.colors,
              primaryColorHover: variants.hoverColor,
              primaryColorDisable: variants.disabledColor,
            },
          },
          stripeConfig: userInput.stripeConfig ? {
            ...userInput.stripeConfig,
            country: userInput.stripeConfig.country?.toLowerCase(),
            currency: userInput.stripeConfig.currency?.toLowerCase(),
          } : userInput.stripeConfig,
          squareConfig: userInput.squareConfig ? {
            ...userInput.squareConfig,
            country: userInput.squareConfig.country?.toUpperCase(),
            currency: userInput.squareConfig.currency?.toUpperCase(),
          } : userInput.squareConfig,
        },
        siteId: userInput.siteid,
      }).unwrap();

      if (payload?.status === "SUCCESS") {
        toast.success(payload?.message);

        const refreshed = await refetch();

        if (refreshed?.data?.data) {
          setUserInput(refreshed.data.data);
        }

        setTimeout(() => {
          navigate("/configuration");
          setPageLoading(false);
        }, 1500);
      } else {
        const invalidKeys = extractInvalidKeys(payload);
        if (Array.isArray(invalidKeys) && invalidKeys.length > 0) {
          setApiErrors(buildInvalidKeyErrors(invalidKeys, payload?.message));
        }
        toast.error(payload?.message || "Update failed");
        setPageLoading(false);
      }
    } catch (error) {
      const invalidKeys = extractInvalidKeys(error?.data);
      if (Array.isArray(invalidKeys) && invalidKeys.length > 0) {
        setApiErrors(buildInvalidKeyErrors(invalidKeys, error?.data?.message));
      }
      captureComponentError(error, { component: "EditConfig", action: "handleSubmit" });
      toast.error(error?.data?.message || "Something went wrong");
      setPageLoading(false);
    }
  };

  const handleCancel = () => {
    if (siteConfigData?.data) setUserInput(siteConfigData.data);
    setPageLoading(true);
    toast.info("All changes discarded");

    setTimeout(() => {
      navigate("/configuration");
      setPageLoading(false);
    }, 1500);
  };

  return (
    <CommonPage>
      <Content>
        <LoadableContent isLoading={isLoading || isLoadingConfig || pageLoading}>
          <MainForm
            userInput={userInput}
            setUserInput={setUserInput}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            apiErrors={apiErrors}
          />
        </LoadableContent>
      </Content>
    </CommonPage>
  );
};

export default Page;
